import { array, number, option, ord, record } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { filter, get, isEmpty, map } from 'lodash/fp';
import { createSelector } from 'reselect';

import { CancellationPolicyTag, DateShift, MultiCurrencyAmount } from '@mews-distributor/common';
import { getBooleanProp, getCollectionIds, values } from '@mews/fp';
import { WallClockDateService } from '@mews/framework';

import { Optional } from '@mews/std';
import { getCategorySortingQuery, getCurrencyCode } from '../../../modules/app/selectors';
import { getCurrentDateServiceCategoryPolicy } from '../../../modules/cancellationPolicies';
import { isServiceHourly } from '../../../modules/entities/services/selectors';
import {
    getSelectedEnterpriseCategoryCityTaxIncludedInPriceOption,
    getSelectedEnterpriseCategoryFeeProducts,
    getSelectedEnterpriseCategoryLowestPrice,
} from '../../../modules/pricing/views/categoriesPricing';
import { getSelectedEnterpriseCategories } from '../../../modules/resourceCategories/selectors';
import { ExtendedCategory } from '../../../modules/resourceCategories/types';
import { getServiceAvailabilitiesInLegacyFormat } from '../../../modules/serviceAvailability/selectors';
import { getServiceCategoryMappings } from '../../../modules/serviceCategoryMappings/selectors';
import { getCurrentDateAvailability, getCurrentDateAvailableCategories } from '../../../selectors/availability';
import { WithNowUtcProps } from '../../../selectors/views/availabilityBlocks';
import { ApplicationState } from '../../../types';
import { Category } from '../../../types/data';

export const getOtherDateSuggestions = createSelector(
    getCurrentDateAvailability,
    getServiceAvailabilitiesInLegacyFormat,
    isServiceHourly,

    (currentAvailability, availability, isHourlyService) => {
        if (isHourlyService) {
            return;
        }
        const suggestions = flow(
            map(({ startDate, endDate, label, groupLabel, availabilityId }) => ({
                startDate: WallClockDateService.parse(startDate),
                endDate: WallClockDateService.parse(endDate),
                groupLabel,
                label: DateShift.getByValue(label),
                availability: availability[availabilityId],
            })),
            /** Component using this selector doesn't take into account that even though the object is not empty, all of its categories can have zero available occupancy. So we do it here. */
            map((suggestion) => {
                const { availability: suggestedAvailability } = suggestion;
                const oldCategories = suggestedAvailability?.categories;
                if (suggestedAvailability == null || oldCategories == null) {
                    return suggestion;
                }
                const categories = pipe(
                    oldCategories,
                    record.filter((category) => (category as $TSFixMe)?.availableCount > 0)
                );

                const newSuggestedAvailability = { ...suggestedAvailability, categories };
                return { ...suggestion, availability: newSuggestedAvailability };
            }),
            filter(({ availability: suggestedAvailability }) => {
                const categories = get('categories', suggestedAvailability);
                return !isEmpty(categories);
            })
        )(currentAvailability.suggestions);

        return suggestions;
    }
);

interface CategoryWithLowestPrice extends Category {
    lowestPrice: MultiCurrencyAmount | null;
    cancellationPolicyTags: CancellationPolicyTag[];
}

type MixedCategory = ExtendedCategory & CategoryWithLowestPrice;

interface CurrentDateAvailableCategoriesWithPricesProps extends WithNowUtcProps {
    showOnlyFlexibleRates: boolean;
}

const getCurrentDateAvailableCategoriesWithPrices = (
    state: ApplicationState,
    { showOnlyFlexibleRates, nowUtc }: CurrentDateAvailableCategoriesWithPricesProps
) =>
    pipe(
        getCurrentDateAvailableCategories(state, {}),
        array.map((category) => ({
            ...category,
            lowestPrice: getSelectedEnterpriseCategoryLowestPrice(state, {
                categoryId: category.id,
                filterOnlyRefundable: showOnlyFlexibleRates ?? false,
            }),
            cancellationPolicyTags: getCurrentDateServiceCategoryPolicy(state, { categoryId: category.id, nowUtc }),
            cityTaxIncludedInPriceOption: getSelectedEnterpriseCategoryCityTaxIncludedInPriceOption(state, {
                category,
            }),
            feeProducts: getSelectedEnterpriseCategoryFeeProducts(state, {
                categoryId: category.id,
            }),
        }))
    );

const sortByPrice = (categorySorting: string, currencyCode: string | undefined) => {
    const sorting = pipe(
        number.Ord,
        ord.contramap((category: MixedCategory) => {
            if (currencyCode == null) {
                return 0;
            }
            return category.lowestPrice?.[currencyCode].grossValue ?? 0;
        })
    );

    if (categorySorting === 'desc') {
        return pipe(sorting, ord.reverse);
    }

    return sorting;
};

const isValidSort = (categorySorting: Optional<string>): categorySorting is string =>
    categorySorting === 'asc' || categorySorting === 'desc';

// Prefer category mapping ordering over category ordering from the API
const byCategoryOrdering = pipe(
    number.Ord,
    ord.contramap((category: MixedCategory | ExtendedCategory) => category.categoryMappingOrdering ?? category.ordering)
);

export const getCategoriesByAvailability = createSelector(
    getCurrentDateAvailableCategoriesWithPrices,
    getSelectedEnterpriseCategories,
    getCategorySortingQuery,
    getCurrencyCode,
    getServiceCategoryMappings,
    getBooleanProp('showOnlyFlexibleRates'),
    (availableCategories, categories, categorySorting, currencyCode, categoryMappings, showOnlyFlexibleRates) => {
        const categoryPriceSorting = isValidSort(categorySorting) ? [sortByPrice(categorySorting, currencyCode)] : [];
        const available = pipe(
            availableCategories,
            array.map<CategoryWithLowestPrice, MixedCategory>((category) => ({
                ...categories?.[category.id],
                ...category,
                categoryMappingOrdering: pipe(
                    categoryMappings,
                    option.fromNullable,
                    option.chainNullableK((categoryMapping) => categoryMapping[category.id]?.ordering),
                    option.getOrElseW(() => null)
                ),
            })),
            array.filter((category) =>
                showOnlyFlexibleRates
                    ? category.cancellationPolicyTags.includes(CancellationPolicyTag.FreeCancellation)
                    : true
            ),
            array.sortBy([...categoryPriceSorting, byCategoryOrdering])
        );

        const availableBeds = pipe(
            available,
            array.reduce(0, (accumulator, availableCategory) => {
                const { availableCount, normalBedCount, extraBedCount } = availableCategory;

                return accumulator + availableCount * (normalBedCount + extraBedCount);
            })
        );

        const unavailable = pipe(
            categories,
            values,
            array.filter((category) => !getCollectionIds(availableCategories).includes(category.id)),
            array.sortBy([byCategoryOrdering])
        );

        return { available, availableBeds, unavailable };
    }
);
